import { getShapeByIndex } from '../utils/shapeUtils'
import ConnectedDotPlotViz from '../vizs/ConnectedDotPlotViz'
import BaseComponent from './BaseComponent'

/**
 * {-EN-}
 * # {componentLabel} (`class {componentClassName}`)
 * The connected dot plot is used to display how values are distributed across categories
 *
 * ### Example Usage
 * ```javascript
 *
 *   import {ConnectedDotPlot} from 'sdv-lib'
 *
 *   // Instantiate the chart with a DOM element, a data Array and a config Object
 *   let myChart = new ConnectedDotPlot($el, data, config);
 *
 * ```
 *
 * ### Data structure {.section-data-structure}
 * The tabular data for this chart should be a table with 2 or more columns.
 *
 * Each row represents a data line. The first column should be the id/label of the stack, and the rest of the columns should represent each of the layers in the stack. Values will be transformed in percentage of the total value.
 *
 * | category   | value1 |  value2  |  value3  |
 * | --------   | ------ |  ------  |  ------  |
 * |   Cat1     |    22.4|      15.4|      43.2|
 * |   Cat2     |    52.3|      29.3|      12.8|
 * |   Cat3     |    12.8|      34.1|      61.4|
 *
 * {.data-table}
 *
 * Each column will be assigned a different color, to distinguish them inside of each stack.
 *
 * {-ES-}
 * # {componentLabelEs} (`class {componentClassName}`)
 * El diagrama de puntos conectados se utiliza para mostrar cómo se distribuyen los valores entre categorías.
 *
 * ### Ejemplo de uso
 * ```javascript
 *
 *   import {ConnectedDotPlot} from 'sdv-lib'
 *
 *   // Instantiate the chart with a DOM element, a data Array and a config Object
 *   let myChart = new ConnectedDotPlot($el, data, config);
 *
 * ```
 *
 * ### Estructura de datos {.section-data-structure}
 * Los datos tabulares de este gráfico deben ser una tabla con 2 o más columnas.
 *
 * Cada fila representa una línea de datos. La primera columna debe ser la identificación/etiqueta de la pila, y el resto de las columnas deben representar cada una de las capas de la pila. Los valores se transformarán en porcentaje del valor total.
 *
 * | categoría   | valor1 |  valor2  |  valor3  |
 * | --------   | ------ |  ------  |  ------  |
 * |   Cat1     |    22.4|      15.4|      43.2|
 * |   Cat2     |    52.3|      29.3|      12.8|
 * |   Cat3     |    12.8|      34.1|      61.4|
 *
 * {.data-table}
 *
 * A cada columna se le asignará un color diferente, para distinguirlas dentro de cada pila.
 * */
export default class ConnectedDotPlotComponent extends BaseComponent {
  static componentLabel = 'Connected Dot Plot'
  static componentLabelEs = 'Gráfico de puntos conectados'
  static vizClass = ConnectedDotPlotViz
  static dataFormat = [
    { index: 0, axis: 'y0', types: [String], role: 'id', roleLabel: 'Category' },
    { index: 1, axis: 'x0', types: [Number], role: 'value', roleLabel: 'Value' },
    {
      index: -1,
      axis: 'x0',
      label: 'Stack',
      types: [Number, null],
      role: 'value',
      roleLabel: 'Stacked value',
    },
  ]
  constructor($root, config, data) {
    super($root, config, data)
  }
  static defaultConfig = {
    title: this.componentLabel,
    TooltipBehavior: {
      axis: {
        x: {
          tooltipValueFormat: {
            exposed: true,
            nonEditable: false,
          },
        },
      },
    },
  }

  static dataRequirements = {
    avoidDates: true,
  }

  static accessibilityRulesTotalItems = Object.values(super.accessibilityRules).length
  static accessibilityRules = {
    ...super.accessibilityRules,
    textures: {
      id: 'shapes',
      order: this.accessibilityRulesTotalItems + 1,
      label: 'rule_shapes_label',
      description: 'rule_shapes_desc',
      sidebarFieldName: 'colorDict',
      check: (config, data) => {
        if (config.ItemsHelper.colorDict) {
          const colorKey =
            typeof config.ItemsHelper.colorKey === 'function' ? 'id' : config.ItemsHelper.colorKey
          const groupedDataKeys = new Set(data.map(d => d[colorKey]))
          let totalShapes = [...groupedDataKeys].reduce((accumulator, current) => {
            accumulator += config.ItemsHelper.colorDict[current]?.shape ? 1 : 0
            return accumulator
          }, 0)
          return totalShapes >= groupedDataKeys.size - 1
        }
        return false
      },
      applyAutoConfigChange: function (config, data, colors) {
        if (this.check(config, data)) return {}
        const oldConfig = {
          ItemsHelper: {
            ...config.ItemsHelper,
            colorDict: config.ItemsHelper.colorDict
              ? {
                  ...config.ItemsHelper.colorDict,
                }
              : undefined,
          },
        }
        const colorKey =
          typeof config.ItemsHelper.colorKey === 'function' ? 'id' : config.ItemsHelper.colorKey
        const groupedDataKeys = new Set(data.map(d => d[colorKey]))
        const newConfig = {
          ItemsHelper: {
            ...oldConfig.ItemsHelper,
            colorDict: [...groupedDataKeys].reduce((accumulator, current, i) => {
              accumulator[current] = {
                ...colors[current],
                shape: getShapeByIndex(i),
              }
              return accumulator
            }, {}),
          },
        }
        return {
          oldConfig,
          newConfig,
        }
      },
    },
  }
}
